import React, {FC, useCallback, useEffect, useRef, useState, useMemo, useLayoutEffect} from 'react';
import userImage from 'assets/images/image1.png';
import ChatHeader from 'components/chatContent/chatHeader/ChatHeader';
import ChatFooter from 'components/chatContent/chatFooter/ChatFooter';
import Styles from 'components/chatContent/ChatContent.module.scss';
import MessageBox from './messageBox/MessageBox';
import { updateParticipantsInRoom, UpdateHostInRoom, sendJoinRequest, ensureUserInPublicRoom } from 'store/services/firebase-broadcast';
import { getCurrentDate, randomString, getMessageTime, getParticipantsArray, getMessageDateLabel, getAttachmentMediaType, findFirstUnreadMessageId, isPublicRoom } from 'helpers/utils';
import joinImage from 'assets/svg/join button.svg';
import moderatePoliticsFavIcon from 'assets/images/mp-splash-icon.png';
import CustomModal from 'components/customModal/CustomModal';
import { useHistory } from 'react-router';
import Button from 'components/button/Button';
import Loader from 'components/loader/loader';
import Toast from 'components/toast/Tost';
import VideoPlayer from 'components/videoPlayer/VideoPlayer';
import {
  JOIN_REQUEST_RESPONSE_EVENT,
  JoinRequestResponseDetail,
} from 'hooks/useJoinRequestNotifications';
import {
  isJoinRequestDeclined,
  clearJoinRequestDeclined,
  markJoinRequestDeclined,
  JOIN_REQUEST_DECLINED_UPDATED_EVENT,
} from 'utils/pendingJoinRequests';
import { useStuckDayKeys } from 'hooks/useStuckDayKeys';
import { useDateTimeFormat } from 'hooks/useDateTimeFormat';
import { useRoomTypers } from 'store/services/typingPresence';
import { useThreadReads } from 'store/services/threadReads';
import TypingLabel from 'components/chatContent/typingLabel/TypingLabel';
import NewMessagesDivider from 'components/chatContent/newMessagesDivider/NewMessagesDivider';
import {
  ensureRoomNewDividerAnchor,
  dismissRoomNewDivider,
  getRoomNewDividerAnchor,
} from 'store/services/newMessagesDividerStore';

interface ChatContentProps {
  viewReplies: Function;
  replyItems: Function;
  chatInfo: Function;
  chats: any;
  selected: any;
  actions: boolean;
  activateRoom?: any;
  // Pagination props for dynamic loading
  hasMore?: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  loadOlderMessages?: () => Promise<void>;
  // Initialization state for scroll control
  isInitialized?: boolean;
  fullPage?: boolean;
  isJoined?: boolean;
  onMenuClick?: () => void;
  allRooms?: any[];
}
const ChatContent: FC<ChatContentProps> = (props: ChatContentProps) => {
  const { viewReplies, replyItems, chatInfo, chats, selected, actions, activateRoom, hasMore, isLoading, isLoadingMore, loadOlderMessages, isInitialized, fullPage, isJoined = false, onMenuClick, allRooms } = props;
  const [images, setImages] = useState<any>([]);
  /** Phone: measured ChatFooter height for typing bar + scroll clearance. */
  const [composerHeight, setComposerHeight] = useState(64);
  const onComposerHeightChange = useCallback((height: number) => {
    setComposerHeight((prev) => (prev === height ? prev : height));
  }, []);
  const composerHeightStyle = useMemo(
    () => ({ ['--composer-height' as string]: `${composerHeight}px` }) as React.CSSProperties,
    [composerHeight]
  );
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userPk = user?.pk;
  const [chatContent] = useState(chats);
  const latestMsgId: any = useRef(null);
  const lastMarkedRef = useRef<string | null>(null); // (room:newestId) already marked read
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoadRef = useRef(true); // Track if this is the very first load
  const scrollPositionRef = useRef(0); // Track scroll position before loading older messages
  const previousScrollHeightRef = useRef(0); // Track previous scroll height
  const pendingRestoreRef = useRef(false); // A page was requested; restore scroll once it lands
  const nearBottomRef = useRef(true); // Was the user at/near the bottom before the last growth?
  const prevChatsLenRef = useRef(0); // Detect appended (new) messages vs prepended (paginated)
  // Discord NEW bar: freeze lastSeen at room open; session cache survives remounts
  // until the user types in the footer.
  const [openLastSeenTime, setOpenLastSeenTime] = useState<number | null>(null);
  const [openLastSeenMessageId, setOpenLastSeenMessageId] = useState<string | null>(null);
  const [newDividerDismissed, setNewDividerDismissed] = useState(false);
  const [showNewMessagesPill, setShowNewMessagesPill] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [modalToggle, setModalToggle] = useState(false);
  const [requestDeclined, setRequestDeclined] = useState(() =>
    isJoinRequestDeclined(userPk, selected?.pk)
  );
  const wasJoinedRef = useRef(isJoined);
  const [toast, setToast] = useState(false);
  const [toastValues, setToastValues] = useState({message: '', type: ''});
  const history = useHistory();
  const typers = useRoomTypers(isJoined ? selected?.pk : null, userPk);
  const hasTypers = typers.length > 0;
  const prevHasTypersRef = useRef(false);
  const { reads: threadReads, isReady: threadReadsReady } = useThreadReads(
    isJoined ? selected?.pk : null,
    userPk
  );
  const { setting: dateTimeSetting } = useDateTimeFormat();

  // Group messages by calendar day. `chats` is already sorted oldest-first, so a
  // linear pass is enough. Each group renders one sticky separator (see .daySection).
  const dayGroups = useMemo(() => {
    const groups: { key: string; label: string; items: any[] }[] = [];
    (chats || []).forEach((item: any) => {
      const ts = getMessageTime(item);
      if (!ts) {
        // No usable timestamp — keep it with the previous day rather than inventing one.
        if (groups.length) groups[groups.length - 1].items.push(item);
        else groups.push({ key: 'undated', label: '', items: [item] });
        return;
      }
      const key = new Date(ts).toDateString();
      const last = groups[groups.length - 1];
      if (!last || last.key !== key) {
        groups.push({ key, label: getMessageDateLabel(ts, { setting: dateTimeSetting }), items: [item] });
      } else {
        last.items.push(item);
      }
    });
    return groups;
  }, [chats, dateTimeSetting]);

  // Ordered media descriptors for the whole conversation (oldest-first, matching
  // the message order). Passed to each MessageBox so clicking an attachment opens
  // the viewer as a gallery with prev/next across all of them.
  const conversationMedia = useMemo(
    () =>
      (chats || [])
        .filter((m: any) => m?.attachments?.[0]?.id)
        .map((m: any) => ({
          id: m.attachments[0].id,
          name: m.attachments[0].name,
          type: getAttachmentMediaType(m.attachments[0]),
        })),
    [chats]
  );

  const firstUnreadMessageId = useMemo(() => {
    if (newDividerDismissed) return null;
    const byTime = findFirstUnreadMessageId(chats, openLastSeenTime, userPk);
    if (byTime) return byTime;
    // Fallback when lastSeenTime is missing but lastSeenMessage id exists.
    if (!openLastSeenMessageId || !chats?.length || userPk == null) return null;
    const myPk = String(userPk);
    const seenIdx = chats.findIndex((m: any) => String(m?.id) === openLastSeenMessageId);
    if (seenIdx === -1) return null;
    for (let i = seenIdx + 1; i < chats.length; i += 1) {
      const m = chats[i];
      const senderId = m?.sender?.id ?? m?.sender?.pk;
      if (senderId != null && String(senderId) === myPk) continue;
      return m?.id != null ? String(m.id) : null;
    }
    return null;
  }, [chats, openLastSeenTime, openLastSeenMessageId, userPk, newDividerDismissed]);

  const dismissNewDivider = useCallback(() => {
    if (!selected?.pk) return;
    const newest = chats?.length ? chats[chats.length - 1] : null;
    const newestTime = newest ? getMessageTime(newest) : null;
    const newestId = newest?.id != null ? String(newest.id) : null;
    dismissRoomNewDivider(selected.pk, newestTime, newestId);
    if (newestTime != null) setOpenLastSeenTime(newestTime);
    if (newestId != null) setOpenLastSeenMessageId(newestId);
    setNewDividerDismissed(true);
  }, [selected?.pk, chats]);

  // The scroll container only mounts once `isJoined` is true, and a ref object's identity
  // never changes — an effect keyed on it would never see the node appear. Track it in
  // state via a callback ref, while still filling chatContainerRef for the scroll logic.
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
  const setChatContainer = useCallback((node: HTMLDivElement | null) => {
    chatContainerRef.current = node;
    setScrollNode(node);
  }, []);

  // Which day separators are currently pinned (CSS has no `:stuck`). Re-attaches the
  // observer whenever the set of day sections changes, e.g. older messages load a new day.
  const groupSignature = useMemo(() => dayGroups.map((g) => g.key).join('|'), [dayGroups]);
  const stuckDays = useStuckDayKeys(scrollNode, groupSignature);

  // Index of the most recent message (consumed by updateLastSeen). Previously computed
  // inline in the render map; hoisted here now that messages render grouped by day.
  useEffect(() => {
    if (!chats.length) return;
    let latest = 0;
    for (let i = 1; i < chats.length; i += 1) {
      if (getMessageTime(chats[i]) > getMessageTime(chats[latest])) latest = i;
    }
    latestMsgId.current = latest;
  }, [chats]);

  useEffect(() => {
    setRequestDeclined(isJoinRequestDeclined(userPk, selected?.pk));
    setModalToggle(false);
  }, [selected?.pk, userPk]);

  useEffect(() => {
    if (isJoined && !wasJoinedRef.current) {
      setModalToggle(false);
      setRequestDeclined(false);
      if (selected?.pk) {
        clearJoinRequestDeclined(userPk, selected.pk);
      }
    }
    wasJoinedRef.current = isJoined;
  }, [isJoined, selected?.pk, userPk]);

  useEffect(() => {
    const handleJoinRequestResponse = (event: Event) => {
      const { roomId, status } = (event as CustomEvent<JoinRequestResponseDetail>).detail;
      if (String(roomId) !== String(selected?.pk)) {
        return;
      }
      if (status === 'accepted') {
        setModalToggle(false);
        setRequestDeclined(false);
        clearJoinRequestDeclined(userPk, roomId);
      } else if (status === 'declined') {
        setModalToggle(false);
        markJoinRequestDeclined(userPk, roomId);
        setRequestDeclined(true);
      }
    };

    const handleDeclinedUpdated = (event: Event) => {
      const { roomId } = (event as CustomEvent<{ roomId: string }>).detail;
      if (String(roomId) === String(selected?.pk)) {
        setRequestDeclined(true);
      }
    };

    window.addEventListener(JOIN_REQUEST_RESPONSE_EVENT, handleJoinRequestResponse);
    window.addEventListener(JOIN_REQUEST_DECLINED_UPDATED_EVENT, handleDeclinedUpdated);
    return () => {
      window.removeEventListener(JOIN_REQUEST_RESPONSE_EVENT, handleJoinRequestResponse);
      window.removeEventListener(JOIN_REQUEST_DECLINED_UPDATED_EVENT, handleDeclinedUpdated);
    };
  }, [selected?.pk, userPk]);

  useEffect(() => {}, [images]);
  
  // NOTE on `scrollNode`: the scroll container is only rendered once `isJoined` is true.
  // Every effect below must therefore key on the *node*, not just on data props —
  // otherwise, when the sidebar has already cached the room, `isInitialized`/`chats.length`
  // are final on mount and nothing re-runs once the container finally appears. That left
  // the list unscrolled and the scroll listener unattached on a room's first open.

  // ADD: Scroll event listener for dynamic loading
  useEffect(() => {
    if (!scrollNode) return undefined;

    const handleScroll = () => {
      // Track whether the user is following the conversation, so incoming messages only
      // auto-scroll when they're already at the bottom (see the new-message effect below).
      const distanceFromBottom =
        scrollNode.scrollHeight - scrollNode.scrollTop - scrollNode.clientHeight;
      nearBottomRef.current = distanceFromBottom < 120;
      if (nearBottomRef.current) setShowNewMessagesPill(false);

      // Load more messages when user scrolls to top (within 50px)
      if (!hasMore || isLoadingMore || !loadOlderMessages) return;
      if (scrollNode.scrollTop < 50) {
        loadOlderMessages();
      }
    };

    scrollNode.addEventListener('scroll', handleScroll);
    return () => scrollNode.removeEventListener('scroll', handleScroll);
  }, [scrollNode, hasMore, isLoadingMore, loadOlderMessages]);

  // Follow new messages. A brand-new message is appended at the bottom, so if the user was
  // already there, keep them there (WhatsApp/Telegram). If they'd scrolled up to read
  // history, don't yank them — surface a "new messages" pill instead.
  useEffect(() => {
    const prevLen = prevChatsLenRef.current;
    prevChatsLenRef.current = chats.length;

    if (!scrollNode || chats.length <= prevLen) return; // no new message
    if (isInitialLoadRef.current) return; // first load handled by the scroll-to-bottom effect
    if (isLoadingMore || pendingRestoreRef.current) return; // paginated (prepended) — not new

    if (nearBottomRef.current) {
      requestAnimationFrame(() => {
        if (scrollNode) scrollNode.scrollTop = scrollNode.scrollHeight;
      });
      setShowNewMessagesPill(false);
    } else {
      setShowNewMessagesPill(true);
    }
  }, [chats.length, scrollNode, isLoadingMore]);

  // Reset per-room tracking so a room switch doesn't count as "new messages".
  useEffect(() => {
    prevChatsLenRef.current = 0;
    nearBottomRef.current = true;
    setShowNewMessagesPill(false);
    prevHasTypersRef.current = false;
  }, [selected?.pk]);

  const scrollToBottom = () => {
    if (!scrollNode) return;
    scrollNode.scrollTop = scrollNode.scrollHeight;
    nearBottomRef.current = true;
    setShowNewMessagesPill(false);
  };

  // Receiver-only: when a remote typer appears, the typing band shrinks/overlays the
  // list. Re-anchor after layout if the user was already following the latest.
  useLayoutEffect(() => {
    const appeared = hasTypers && !prevHasTypersRef.current;
    prevHasTypersRef.current = hasTypers;
    if (!scrollNode || !appeared || !nearBottomRef.current) return undefined;

    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled || !scrollNode) return;
        // Prefer sentinel so bottom padding / typing clearance are included.
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ block: 'end', inline: 'nearest' });
        }
        scrollNode.scrollTop = scrollNode.scrollHeight;
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [hasTypers, scrollNode]);

  // The handler above is the ONLY pagination trigger, and it needs a `scroll` event —
  // which the browser never fires when the list is shorter than its container. Keep
  // pulling pages until there's something to scroll (or the room is exhausted).
  useEffect(() => {
    if (!scrollNode || !isInitialized || !hasMore || isLoadingMore || !loadOlderMessages) return;
    if (scrollNode.scrollHeight <= scrollNode.clientHeight) {
      loadOlderMessages();
    }
  }, [scrollNode, chats.length, isInitialized, hasMore, isLoadingMore, loadOlderMessages]);

  // Save scroll position when starting to load older messages
  useEffect(() => {
    if (!scrollNode || !isLoadingMore) return;

    scrollPositionRef.current = scrollNode.scrollTop;
    previousScrollHeightRef.current = scrollNode.scrollHeight;
    pendingRestoreRef.current = true;
  }, [scrollNode, isLoadingMore]);

  // Restore scroll position after older messages are prepended
  useLayoutEffect(() => {
    // A boolean flag, not `scrollPositionRef.current === 0`: pagination fires at
    // scrollTop < 50 — usually exactly 0 — so that guard skipped the restore in the
    // very case it exists for, and the view jumped.
    //
    // `isInitialLoadRef` guard: on a room's first open the auto-load above can fire
    // before the initial scroll-to-bottom has settled. Without this, the restore (on a
    // 50ms timeout) lands last and fights it.
    if (!scrollNode || isLoadingMore || !pendingRestoreRef.current || isInitialLoadRef.current) {
      return undefined;
    }

    // Use setTimeout to ensure DOM has been updated
    const timeoutId = setTimeout(() => {
      // Keep the same content under the user's eyes: the list grew at the top, so shift
      // down by exactly how much it grew.
      const grewBy = scrollNode.scrollHeight - previousScrollHeightRef.current;
      scrollNode.scrollTop = scrollPositionRef.current + grewBy;

      // Reset tracking values
      pendingRestoreRef.current = false;
      scrollPositionRef.current = 0;
      previousScrollHeightRef.current = 0;
    }, 50); // Increased delay for reliable DOM updates

    return () => clearTimeout(timeoutId);
  }, [scrollNode, isLoadingMore]); // Only depend on isLoadingMore, not chats.length

  // Snapshot / restore NEW-divider anchor when the room changes.
  // Session + sessionStorage keep the bar across remounts/refresh; typing advances
  // the cursor so the same unread batch does not return.
  useLayoutEffect(() => {
    isInitialLoadRef.current = true;
    if (!selected?.pk || userPk == null) {
      setOpenLastSeenTime(null);
      setOpenLastSeenMessageId(null);
      setNewDividerDismissed(false);
      return;
    }

    const cached = getRoomNewDividerAnchor(selected.pk);
    if (cached && !cached.dismissed) {
      setOpenLastSeenTime(cached.lastSeenTime);
      setOpenLastSeenMessageId(cached.lastSeenMessageId);
      setNewDividerDismissed(false);
      return;
    }

    let lastSeen: number | null = null;
    let lastSeenMsgId: string | null = null;
    if (selected?.host?.pk === userPk) {
      lastSeen =
        typeof selected.host?.lastSeenTime === 'number' ? selected.host.lastSeenTime : null;
      lastSeenMsgId = selected.host?.lastSeenMessage
        ? String(selected.host.lastSeenMessage)
        : null;
    } else {
      const me = getParticipantsArray(selected?.participants).find(
        (p: any) => p?.pk === userPk
      );
      lastSeen = typeof me?.lastSeenTime === 'number' ? me.lastSeenTime : null;
      lastSeenMsgId = me?.lastSeenMessage ? String(me.lastSeenMessage) : null;
    }

    const anchor = ensureRoomNewDividerAnchor(selected.pk, lastSeen, lastSeenMsgId);
    setOpenLastSeenTime(anchor.lastSeenTime);
    setOpenLastSeenMessageId(anchor.lastSeenMessageId);
    setNewDividerDismissed(anchor.dismissed);
  }, [selected?.pk, userPk]);

  // Initial scroll: to NEW divider when there are unread messages, else bottom.
  useLayoutEffect(() => {
    if (!scrollNode || !isInitialized || !chats.length || isLoadingMore) return;
    if (!isInitialLoadRef.current) return;

    const container = scrollNode;

    const scrollToInitialTarget = () => {
      if (firstUnreadMessageId) {
        const divider = container.querySelector(
          '[data-new-messages-divider]'
        ) as HTMLElement | null;
        if (divider) {
          divider.scrollIntoView({ block: 'start' });
          nearBottomRef.current = false;
          return;
        }
      }
      container.scrollTop = container.scrollHeight;
      nearBottomRef.current = true;
    };

    scrollToInitialTarget();

    let resizeObserver: ResizeObserver;
    let timeoutId: NodeJS.Timeout;

    const waitForStability = () => {
      let lastHeight = container.scrollHeight;
      let stableCount = 0;
      let observerTriggered = false;
      const requiredStable = 2;

      resizeObserver = new ResizeObserver(() => {
        observerTriggered = true;
        const currentHeight = container.scrollHeight;

        if (currentHeight === lastHeight) {
          if (stableCount === 0) {
            scrollToInitialTarget();
            resizeObserver.disconnect();
            isInitialLoadRef.current = false;
            return;
          }

          stableCount += 1;
          if (stableCount >= requiredStable) {
            scrollToInitialTarget();
            resizeObserver.disconnect();
            isInitialLoadRef.current = false;
          }
        } else {
          stableCount = 0;
          lastHeight = currentHeight;
          scrollToInitialTarget();
        }
      });

      resizeObserver.observe(container);

      const checkTimeout = setTimeout(() => {
        if (!observerTriggered) {
          scrollToInitialTarget();
          resizeObserver?.disconnect();
          isInitialLoadRef.current = false;
        }
      }, 500);

      timeoutId = setTimeout(() => {
        scrollToInitialTarget();
        resizeObserver?.disconnect();
        isInitialLoadRef.current = false;
      }, 2000);

      return () => {
        clearTimeout(checkTimeout);
      };
    };

    const cleanup = waitForStability();

    return () => {
      resizeObserver?.disconnect();
      clearTimeout(timeoutId);
      cleanup?.();
    };
  }, [
    scrollNode,
    isInitialized,
    chats.length,
    isLoadingMore,
    selected?.pk,
    firstUnreadMessageId,
  ]);

  // Memoized calculations to prevent unnecessary re-computations
  const { chatsPin, pinnedCount } = useMemo(() => {
    if (!chats || chats.length === 0) {
      return { chatsPin: [], pinnedCount: 0 };
    }

    const pinned = chats.filter((r: any) => r.pin);
    const count = pinned.length;

    return { chatsPin: pinned, pinnedCount: count };
  }, [chats]);

  // Mark the open room read: persist the newest message as my lastSeenMessage / lastSeenTime.
  // Being *in* an open room is what marks it read (Telegram/WhatsApp semantics), and since
  // this fires whenever `chats` changes it also covers messages arriving while I watch, and
  // my own sent messages. Guards on the current value so it doesn't re-write in a loop.
  const markRoomRead = useCallback(() => {
    if (!selected?.pk || !isJoined || !chats.length) return;

    const newest = chats[chats.length - 1]; // chats is sorted oldest-first
    const newestId = newest?.id;
    if (!newestId) return;

    const newestTime = getMessageTime(newest);
    // The room's current count = everything read up to now (exact unread = count - this).
    // It arrives via the rooms listener, which can trail the message by a tick — so the
    // mark key includes it, and we re-write once it catches up (converges within 1-2 writes).
    const seenCount = typeof selected?.messageCount === 'number' ? selected.messageCount : 0;

    // `selected` only reflects the write after a Firebase round-trip, so guard locally to
    // avoid re-writing the identical marker while that's in flight.
    const markKey = `${selected.pk}:${newestId}:${seenCount}`;
    if (lastMarkedRef.current === markKey) return;
    lastMarkedRef.current = markKey;

    const ImHost = selected?.host?.pk === user?.pk;
    if (ImHost) {
      const cur = selected.host;
      if (cur?.lastSeenMessage === newestId && cur?.lastSeenCount === seenCount) return;
      UpdateHostInRoom({
        roomId: selected.pk,
        host: {
          ...cur,
          lastSeenMessage: newestId,
          lastSeenTime: newestTime,
          lastSeenCount: seenCount,
        },
      });
    } else {
      const participants = getParticipantsArray(selected?.participants);
      const meIndex = participants.findIndex((p: any) => p?.pk === user?.pk);
      if (meIndex === -1) return;
      const cur = participants[meIndex];
      if (cur?.lastSeenMessage === newestId && cur?.lastSeenCount === seenCount) return;
      const updated = participants.map((p: any, i: number) =>
        i === meIndex
          ? { ...p, lastSeenMessage: newestId, lastSeenTime: newestTime, lastSeenCount: seenCount }
          : p
      );
      updateParticipantsInRoom({ roomId: selected.pk, user: updated });
    }
  }, [selected, isJoined, chats, user?.pk]);

  // Fires on open, on each new incoming message, and on send — all mark the room read.
  useEffect(() => {
    markRoomRead();
  }, [markRoomRead]);

  const closeDialog = () => {
    document.body.classList.remove('modal-open');
  };

  const closeModal = () => {
    setModalToggle(false);
  };

  const handleJoinRoom = () => {
    // Public rooms: join immediately — no host approval.
    if (isPublicRoom(selected, allRooms)) {
      ensureUserInPublicRoom(selected, user, allRooms)
        .then(() => {
          setToastValues({
            message: 'You joined this public room',
            type: 'Success',
          });
          setToast(true);
        })
        .catch((error) => {
          if (error?.code === 'INVALID_USER_DATA') return;
          setToastValues({
            message: 'Something went wrong!',
            type: 'Error',
          });
          setToast(true);
        });
      return;
    }

    const request = {
      roomId: selected.pk,
      id: `${selected.pk}-${randomString()}`,
      user: user,
      time: getCurrentDate(),
      timestamp: Date.now(),
      roomTopic: selected.topic,
    };

    sendJoinRequest(request).then((e) => {
      setRequestDeclined(false);
      setToastValues({
        message: 'Join request has been sent',
        type: 'Success',
      });
      setModalToggle(true);
      setToast(true);
    }).catch((error) => {
      // Skip handling if user data is invalid (toast already shown by middleware)
      if (error.code === 'INVALID_USER_DATA') {
        return;
      }
      
      // Handle duplicate request error
      if (error.code === 'DUPLICATE_REQUEST') {
        setToastValues({
          message: 'You already have a pending request for this room. Please wait for the host to respond.',
          type: 'Warning',
        });
        setToast(true);
      } else {
        setToastValues({
          message: 'Something went wrong!',
          type: 'Error',
        });
        setToast(true);
      }
    });
  };

  const joinToggleMsg = () => (
    <div className={Styles.modalTopConatiner}>
      <div>
        <img
          src={moderatePoliticsFavIcon}
          alt="chat info"
          className={Styles.joinImage}
        />
        <div className={`${Styles.modalBottomContent} text-center`}>
          We'll notify you when they accept. Until then browse some more room
          topics.
        </div>
        <div className="text-center">
        <Button
          label="BROWSE ROOM TOPICS"
          clickHandler={() => history.push("conversations-nearby")}
          cssClass={`${Styles.createRoomWidth}`}
        />
        </div>
      </div>
    </div>
  );

  const onMessageSent = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    // Ensure the newly sent message is in the DOM before scrolling
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  };

  return (
    <>
      <ChatHeader
        key={`header-${selected?.pk || 'no-room'}`}
        name={selected?.topic ?? 'Chat room'}
        chatInfo={chatInfo}
        selected={selected}
        isJoined={isJoined}
        onMenuClick={onMenuClick}
      />
      {!isJoined ? (
        <div className={Styles.joinRoom}>
          {selected?.pk && (
            <div className={Styles.joinButtonContainer}>
              <div
                onClick={handleJoinRoom}
                onKeyPress={handleJoinRoom}
                role="button"
                tabIndex={0}
                >
                <img
                  src={joinImage}
                  alt="chat info"
                  className={Styles['chatInfoImage-big']}
                  />
              </div>
            </div>
          )}
          {requestDeclined && (
            <div className={Styles.joinRequestDeclined}>
              <p>Your request was declined by the host.</p>
              <p>You can send a new request again.</p>
            </div>
          )}
          <p>Join this room to start chatting</p>
          
          {/* Intro video below join button */}
          {selected?.intro_video_url && (
            <div className={Styles.introVideoContainer}>
              <VideoPlayer url={selected.intro_video_url} />
            </div>
          )}
          
        </div>
      ) : (
        <>
          <div
            ref={setChatContainer}
            className={`customScrollBar ${Styles.chatContentWrapper} ${
              fullPage ? Styles.chatContentWrapperFullPage : ''
            } ${hasTypers ? Styles.hasTypingClearance : ''}`}
            style={composerHeightStyle}
          >
            {/* Loading spinner for older messages */}
            {isLoadingMore && <Loader spinner={Styles.topLoader} aligned />}

            {/* Switching rooms: the new room's messages haven't landed yet. `chats` is
                already empty (it's room-tagged), so show a spinner, not a blank list. */}
            {isLoading && chats.length === 0 && <Loader spinner={Styles.loader} aligned />}

            {/* One section per calendar day; its separator sticks to the top while the
                day is on screen, then the next day's separator pushes it out. */}
            {dayGroups.map((group) => (
              <div className={Styles.daySection} key={group.key}>
                {/* Probe for the pinned state — see useStuckDayKeys */}
                <div
                  className={Styles.stickySentinel}
                  data-day-sentinel={group.key}
                  aria-hidden="true"
                />
                {group.label && (
                  <div
                    className={`${Styles.dateSeparator} ${
                      stuckDays.has(group.key) ? Styles.isStuck : ''
                    }`}
                  >
                    <span>{group.label}</span>
                  </div>
                )}
                {group.items.map((item: any, index: number) => (
                  <React.Fragment
                    key={item.id || `${item.timestamp || item.createdTime}-${item.sender?.pk || index}`}
                  >
                    {firstUnreadMessageId &&
                      String(item.id) === firstUnreadMessageId && <NewMessagesDivider />}
                    <MessageBox
                      selected={selected}
                      item={item}
                      replyItems={replyItems}
                      type=""
                      viewReplies={viewReplies}
                      actions={actions}
                      chatsPin={chatsPin}
                      pinCount={pinnedCount}
                      conversationMedia={conversationMedia}
                      threadRead={item?.id ? threadReads[item.id] : undefined}
                      threadReadsReady={threadReadsReady}
                    />
                  </React.Fragment>
                ))}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          {showNewMessagesPill && (
            <button
              type="button"
              className={`${Styles.newMessagesPill} ${
                hasTypers ? Styles.newMessagesPillAboveTyping : ''
              }`}
              onClick={scrollToBottom}
            >
              New messages ↓
            </button>
          )}
          <TypingLabel typers={typers} style={composerHeightStyle} />
          <ChatFooter
            images={(e: string) => setImages([...images, e])}
            clearImages={() => setImages([])}
            attachedImages={images}
            chats={chatContent}
            selected={selected}
            activateRoom={activateRoom}
            onMessageSent={onMessageSent}
            onComposerInteract={dismissNewDivider}
            onHeightChange={onComposerHeightChange}
          />
        </>
      )}

      {modalToggle && (
        <CustomModal
          modalTitle={
            "Your request to join this room has been sent to the host"
          }
          wrapperClass={Styles.publicProfileWrapper}
          heightClass={Styles.publicProfileBody}
          closeDialog={closeDialog}
          modalTitleClass={Styles.modalTitleClass}
          closeModal={closeModal}
          close
        >
          {joinToggleMsg()}
        </CustomModal>
      )}
      {toast && (
        <Toast
          message={toastValues.message}
          position="topRight"
          type={toastValues.type}
          handleCloseToast={setToast}
        />
      )}
    </>
  );
};
export default ChatContent;
